import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import React, { useContext, useEffect } from "react";
import PropTypes from "prop-types";
import {
    ExpandCollapseContainer,
    ResponsiveButtonContainer,
    ResponsiveTotalContainer,
    StyledTimesheetControl,
} from "./SubmitTimesheet.styles";
import { format, startOfWeek } from "date-fns";

import { COLOR_VALUES } from "shared/config/colors";
import CircularProgress from "@material-ui/core/CircularProgress";
import HStack from "shared/components/layout/stack/HStack";
import Icon from "shared/components/utils/Icon";
import PrimaryButton from "shared/components/inputs/button/PrimaryButton";
import TertiaryButton from "shared/components/inputs/button/TertiaryButton";
import SecondaryButton from "shared/components/inputs/button/SecondaryButton";
import SessionContext from "app/contexts/SessionContext";
import SubmitTimesheetContext from "features/timesheet/contexts/SubmitTimesheetContext";
import Text from "shared/components/data-display/text/Text";
import TimesheetGridContext from "features/timesheet/contexts/TimesheetGridContext";
import { TimesheetProjectRowExpansionContextActions } from "features/timesheet/contexts/TimesheetProjectRowExpansionContext";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import VStack from "shared/components/layout/stack/VStack";
import WeekNavigatorContext from "shared/contexts/WeekNavigatorContext";
import useSmallBreakpoint from "shared/hooks/ui/useSmallBreakpoint";
import { useWeeksTimeEntriesApi } from "features/timesheet/timesheet-grid/useTimesheetGrid";

const now = new Date();
export const currentWeekStartDate = format(
    startOfWeek(now, { weekStartsOn: 1 }),
    "yyyy-M-d"
);

export const previousWeekStartDate = format(
    startOfWeek(
        new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7),
        { weekStartsOn: 1 }
    ),
    "yyyy-M-d"
);

const SubmitTimesheet = ({ isStuck = false }) => {
    const session = useContext(SessionContext);
    const { selectedTimesheetStartDate, pendingTimesheetCalendarDate } =
        useContext(WeekNavigatorContext);
    const isSmallScreen = useSmallBreakpoint();

    const {
        selectedDateTimesheetStatus,
        setSelectedDateTimesheetStatus,
        hoursUpdateInProgress,
        hoursUpdateState,
        setHoursUpdateState,
    } = useContext(SubmitTimesheetContext);
    const { setPendingTimesheetFlag } = useContext(WeekNavigatorContext);
    const { disableSubmitTimesheetButton, totalOfWeekHours, allClientIds } =
        useContext(TimesheetGridContext);

    const { openAllProjectRows, closeAllProjectRows } = useContext(
        TimesheetProjectRowExpansionContextActions
    );

    const { useWeeksTimeEntries, submitTimesheet, reopenTimesheet } =
        useWeeksTimeEntriesApi();

    let fromDateVal = selectedTimesheetStartDate ?? today(getLocalTimeZone());
    let formatFromDateVal = `${fromDateVal.year}-${fromDateVal.month}-${fromDateVal.day}`;

    const requestData = {
        employeeId: session.legacyId,
        fromDate: formatFromDateVal,
    };

    const userData = useWeeksTimeEntries(requestData);

    useEffect(() => {
        if (hoursUpdateState) {
            setHoursUpdateState(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTimesheetStartDate]);

    useEffect(() => {
        let formatPendingTImesheetDate = `${pendingTimesheetCalendarDate?.year}-${pendingTimesheetCalendarDate?.month}-${pendingTimesheetCalendarDate?.day}`;
        if (formatPendingTImesheetDate === formatFromDateVal) {
            setPendingTimesheetFlag(true);
        } else {
            setPendingTimesheetFlag(false);
        }
    }, [
        selectedTimesheetStartDate,
        formatFromDateVal,
        pendingTimesheetCalendarDate?.year,
        pendingTimesheetCalendarDate?.month,
        pendingTimesheetCalendarDate?.day,
        setPendingTimesheetFlag,
    ]);

    useEffect(() => {
        if (selectedTimesheetStartDate) {
            const completed = userData?.data
                ?.filter((data) => {
                    let filterDate = new CalendarDate(
                        "AD",
                        format(new Date(data.date), "yyyy"),
                        format(new Date(data.date), "M"),
                        format(new Date(data.date), "d")
                    );
                    let formatFilterDate = `${filterDate.year}-${filterDate.month}-${filterDate.day}`;

                    return formatFromDateVal === formatFilterDate;
                })
                .some((filterData) => filterData?.completed);
            setSelectedDateTimesheetStatus(completed);
        }
    }, [
        userData,
        setSelectedDateTimesheetStatus,
        formatFromDateVal,
        selectedTimesheetStartDate,
    ]);

    const modifier = selectedDateTimesheetStatus
        ? reopenTimesheet
        : submitTimesheet;

    const handleSubmit = async () => {
        const postData = {
            employeeId: session.legacyId,
            weekDate: fromDateVal,
        };

        await modifier.mutateAsync(postData);
    };

    const expandCollapseButtons = (
        <ExpandCollapseContainer spacing="Zero_5" align="center">
            <TertiaryButton
                label="Expand"
                leadingIcon="expand"
                aria-label="Expand All"
                onClick={openAllProjectRows}
            />
            <TertiaryButton
                label="Collapse"
                leadingIcon="collapse"
                aria-label="Collapse All"
                onClick={() => closeAllProjectRows(allClientIds)}
            />
        </ExpandCollapseContainer>
    );

    const submitButton = (
        <ResponsiveButtonContainer $isStuck={isStuck}>
            {(submitTimesheet.isLoading || reopenTimesheet.isLoading) && (
                <CircularProgress
                    style={{
                        height: "16px",
                        width: "16px",
                        marginRight: "8px",
                        alignSelf: "center",
                    }}
                />
            )}
            {selectedDateTimesheetStatus ? (
                (previousWeekStartDate === formatFromDateVal ||
                    currentWeekStartDate === formatFromDateVal ||
                    new Date(currentWeekStartDate) <
                        new Date(formatFromDateVal)) && (
                    <SecondaryButton
                        id="reopen-timesheet-button"
                        className="ga4-reopen-timesheet"
                        label="Re-Open Timesheet"
                        leadingIcon="edit"
                        onClick={handleSubmit}
                        disabled={reopenTimesheet.isLoading}
                    />
                )
            ) : (
                <PrimaryButton
                    id="submit-timesheet-button"
                    className="ga4-submit-timesheet"
                    label="Submit Timesheet"
                    onClick={handleSubmit}
                    disabled={
                        totalOfWeekHours === 0 ||
                        disableSubmitTimesheetButton ||
                        submitTimesheet.isLoading
                    }
                />
            )}
        </ResponsiveButtonContainer>
    );

    const timesheetControl = (
        <StyledTimesheetControl>
            <ResponsiveTotalContainer>
                {isSmallScreen ? (
                    <HStack spacing="Zero_25" align="center">
                        {hoursUpdateState && (
                            <>
                                {hoursUpdateInProgress ? (
                                    <Tooltip text="Saving..." enterDelay={0}>
                                        <div>
                                            <CircularProgress
                                                style={{
                                                    color: COLOR_VALUES.Gray_4,
                                                    height: "16px",
                                                    width: "16px",
                                                }}
                                            />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        text="Changes Saved"
                                        enterDelay={0}
                                    >
                                        <div>
                                            <Icon
                                                name="success"
                                                color="Gray_2"
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        <Text variant="Body_1_1" color="Gray_1">
                            Total this Week:
                        </Text>
                        <Text
                            variant="Body_1_2"
                            color="Gray_1"
                            style={{ fontWeight: 700 }}
                        >
                            {totalOfWeekHours} Hours
                        </Text>
                    </HStack>
                ) : (
                    <HStack spacing="Zero_25" align="center">
                        {hoursUpdateState && (
                            <>
                                {hoursUpdateInProgress ? (
                                    <Tooltip text="Saving..." enterDelay={0}>
                                        <div>
                                            <CircularProgress
                                                style={{
                                                    color: COLOR_VALUES.Gray_4,
                                                    height: "16px",
                                                    width: "16px",
                                                }}
                                            />
                                        </div>
                                    </Tooltip>
                                ) : (
                                    <Tooltip
                                        text="Changes Saved"
                                        enterDelay={0}
                                    >
                                        <div>
                                            <Icon
                                                name="success"
                                                color="Gray_2"
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                            </>
                        )}
                        <Text variant="Body_1_1" color="Gray_2">
                            Total for this Week:
                        </Text>
                        <Text
                            variant="Body_1_2"
                            color="Black"
                            style={{ fontWeight: 700 }}
                        >
                            {totalOfWeekHours} Hours
                        </Text>
                    </HStack>
                )}
            </ResponsiveTotalContainer>
            {!isSmallScreen && submitButton}
        </StyledTimesheetControl>
    );

    return (
        <VStack spacing="Two" align={isSmallScreen ? "flex-start" : "flex-end"}>
            {isSmallScreen ? (
                <VStack
                    spacing="Zero_5"
                    align="flex-start"
                    style={{ width: "100%" }}
                >
                    <HStack
                        spacing="Zero"
                        align="center"
                        style={{
                            width: "100%",
                            justifyContent: "space-between",
                        }}
                    >
                        {expandCollapseButtons}
                        {submitButton}
                    </HStack>
                    {timesheetControl}
                </VStack>
            ) : (
                <HStack
                    spacing="Zero"
                    align="center"
                    style={{ width: "100%", justifyContent: "space-between" }}
                >
                    {expandCollapseButtons}
                    {timesheetControl}
                </HStack>
            )}
        </VStack>
    );
};

SubmitTimesheet.propTypes = {
    isStuck: PropTypes.bool,
};

export default SubmitTimesheet;
